let vendorPromise = null;
let initPromise = null;

function loadVendor() {
	if (window.UnicornStudio?.init) {
		return Promise.resolve(window.UnicornStudio);
	}

	if (vendorPromise) {
		return vendorPromise;
	}

	vendorPromise = new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = '/assets/vendor/unicorn-studio-v1.4.18.js';
		script.async = true;
		script.onload = () => resolve(window.UnicornStudio);
		script.onerror = () => {
			vendorPromise = null;
			reject(new Error('Failed to load Unicorn Studio vendor script'));
		};
		(document.head || document.body).appendChild(script);
	});

	return vendorPromise;
}

function runUnicornInit() {
	return loadVendor().then((UnicornStudio) => {
		if (!UnicornStudio?.init) {
			return UnicornStudio;
		}

		const result = UnicornStudio.init();
		return Promise.resolve(result).then(() => UnicornStudio);
	});
}

function ensureUnicornInit() {
	if (!initPromise) {
		initPromise = runUnicornInit().catch((error) => {
			initPromise = null;
			console.error(error);
			throw error;
		});
	}

	return initPromise;
}

function freezeUnicornForNavigation() {
	const scenes = window.UnicornStudio?.scenes || [];
	for (const scene of scenes) {
		scene.curtain?.disableDrawing?.();
	}
	document.querySelectorAll('uni-studio').forEach((el) => {
		el.style.visibility = 'hidden';
	});
}

function findScene(instance) {
	if (!window.UnicornStudio?.scenes || !instance?.container) {
		return null;
	}

	return window.UnicornStudio.scenes.find((scene) =>
		scene.element === instance.container
		|| scene.element?.contains?.(instance.container)
	) || null;
}

class UniStudio extends HTMLElement {
	#abort = null;
	#resizeTimeout = null;
	#initialized = false;

	constructor() {
		super();
		this.container = null;
		this.sceneId = null;
		this.resizeHandler = null;
	}

	connectedCallback() {
		this.#abort = new AbortController();
		const { signal } = this.#abort;

		this.sceneId = this.getAttribute('scene-id') || '';
		this.container = document.createElement('div');
		this.container.style.width = '100vw';
		this.container.style.height = '100vh';
		this.container.dataset.usProjectSrc = this.getAttribute('project-src') || '';
		this.container.dataset.sceneId = this.sceneId;
		this.container.dataset.usDisablemobile = this.getAttribute('disable-mobile') || 'false';
		this.appendChild(this.container);

		this.setupResizeHandler();

		window.addEventListener('tt:transition-complete', this.#onTransitionComplete, { signal });
		window.addEventListener('pageshow', this.#onPageShow, { signal });
		window.addEventListener('pageswap', freezeUnicornForNavigation, { signal });
		window.addEventListener('pagehide', freezeUnicornForNavigation, { signal });

		if (!document.body.classList.contains('tt-transition')) {
			this.#scheduleInit();
		}
	}

	disconnectedCallback() {
		this.#abort?.abort();
		this.#abort = null;

		if (this.resizeHandler) {
			window.removeEventListener('resize', this.resizeHandler);
			this.resizeHandler = null;
		}

		if (this.#resizeTimeout) {
			clearTimeout(this.#resizeTimeout);
			this.#resizeTimeout = null;
		}
	}

	#scheduleInit() {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.#initScene();
			});
		});
	}

	#initScene() {
		if (!this.container) {
			return;
		}

		if (this.#initialized && findScene(this)) {
			return;
		}

		ensureUnicornInit()
			.then(() => {
				if (!this.isConnected || !this.container) {
					return;
				}

				const existing = findScene(this);
				if (existing) {
					this.#initialized = true;
					this.refreshScene(existing);
					return;
				}

				return runUnicornInit().then(() => {
					if (!this.isConnected) {
						return;
					}

					const scene = findScene(this);
					if (scene) {
						this.#initialized = true;
						this.refreshScene(scene);
					}
				});
			})
			.catch(() => {
				this.#initialized = false;
			});
	}

	#onTransitionComplete = () => {
		const scene = findScene(this);
		if (scene) {
			this.#initialized = true;
			this.refreshScene(scene);
			return;
		}

		this.#initialized = false;
		this.#scheduleInit();
	};

	#onPageShow = (event) => {
		if (event.persisted) {
			this.#initialized = false;
			this.container?.removeAttribute('data-us-initialized');
			this.#scheduleInit();
		}
	};

	setupResizeHandler() {
		this.resizeHandler = () => {
			if (this.#resizeTimeout) {
				clearTimeout(this.#resizeTimeout);
			}

			this.#resizeTimeout = setTimeout(() => {
				const scene = findScene(this);
				if (scene) {
					this.refreshScene(scene);
				}
			}, 150);
		};

		window.addEventListener('resize', this.resizeHandler, { passive: true });
	}

	refreshScene(scene) {
		if (!scene) {
			return;
		}

		if (typeof scene.refresh === 'function') {
			scene.refresh();
			return;
		}

		if (typeof scene.resize === 'function') {
			scene.resize();
		}

		if (scene.curtain?.resize) {
			scene.curtain.resize();
		}
	}
}

customElements.define('uni-studio', UniStudio);
